import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  Scissors,
  Loader2,
  ScanLine,
  Play,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  Palette,
  Puzzle,
  Timer,
  Image as ImageIcon,
  Package,
  User,
  AlertTriangle,
  X,
} from 'lucide-react';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  customer: string;
  quantity: number;
  priority: string;
  status: string;
  workflow_type: string;
  source_shelf_id: string | null;
  requires_paint: boolean;
  requires_inserts: boolean;
  is_composite: boolean;
  parent_work_order_id: string | null;
  created_at: string;
  started_at: string | null;
  product_model_id: string | null;
  estimated_duration_minutes: number | null;
  source_shelf?: {
    id: string;
    code: string;
    name: string;
    min_skill_level: string;
  };
  product_model?: {
    image_url: string | null;
    code: string;
    description: string | null;
  };
}

interface Shelf {
  id: string;
  code: string;
  name: string;
  min_skill_level: 'A' | 'B' | 'C';
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800 border-gray-300',
  normal: 'bg-blue-100 text-blue-800 border-blue-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  urgent: 'bg-red-100 text-red-800 border-red-300',
};

const SKILL_LEVEL_CONFIG: Record<string, { bg: string; border: string; text: string; label: string }> = {
  A: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', label: 'Complejo' },
  B: { bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', label: 'Intermedio' },
  C: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', label: 'Básico' },
};

const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default function CuttingStation() {
  const { toast } = useToast();
  const { user } = useAuth();
  const nfcInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [activeWO, setActiveWO] = useState<WorkOrder | null>(null);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [nfcInput, setNfcInput] = useState('');
  const [operatorLevel, setOperatorLevel] = useState<'A' | 'B' | 'C'>('C');

  // Cronómetro
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerStatus, setTimerStatus] = useState<'normal' | 'warning' | 'exceeded'>('normal');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Diálogos
  const [workNotes, setWorkNotes] = useState('');
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Enfocar input NFC al cargar
  useEffect(() => {
    if (nfcInputRef.current && !activeWO) {
      nfcInputRef.current.focus();
    }
  }, [activeWO]);

  // Cronómetro
  useEffect(() => {
    if (activeWO?.started_at) {
      const startTime = new Date(activeWO.started_at).getTime();

      const updateTimer = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsed);

        // Calcular estado del cronómetro
        const estimatedMinutes = activeWO.estimated_duration_minutes || 60;
        const estimatedSeconds = estimatedMinutes * 60;
        const percentage = (elapsed / estimatedSeconds) * 100;

        if (percentage >= 100) {
          setTimerStatus('exceeded');
        } else if (percentage >= 80) {
          setTimerStatus('warning');
        } else {
          setTimerStatus('normal');
        }
      };

      updateTimer();
      timerRef.current = setInterval(updateTimer, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [activeWO]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar nivel del operario
      if (user?.id) {
        const { data: profile } = await api
          .from('profiles')
          .select('skill_level')
          .eq('id', user.id)
          .single();
        if (profile?.skill_level) {
          setOperatorLevel(profile.skill_level);
        }
      }

      // Cargar OTs de corte con producto
      const { data: woData, error: woError } = await api
        .from('work_orders')
        .select(`
          *,
          source_shelf:workshop_shelves!work_orders_source_shelf_id_fkey(
            id, code, name, min_skill_level
          ),
          product_model:product_models!work_orders_product_model_id_fkey(
            image_url, code, description
          )
        `)
        .eq('workflow_type', 'cutting')
        .in('status', ['pending', 'in_progress'])
        .order('priority', { ascending: false })
        .order('created_at');

      if (woError) throw woError;

      // Cargar estanterías
      const { data: shelvesData } = await api
        .from('workshop_shelves')
        .select('*')
        .eq('is_active', true)
        .in('min_skill_level', ['A', 'B', 'C'])
        .order('min_skill_level');

      setShelves(shelvesData || []);
      setWorkOrders(woData || []);

      // Buscar OT activa del operario
      const active = (woData || []).find(
        (wo: WorkOrder) => wo.status === 'in_progress' && (wo as any).assigned_to === user?.id
      );
      if (active) {
        setActiveWO(active);
        setWorkNotes((active as any).notes || '');
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error cargando datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNfcScan = async () => {
    if (!nfcInput.trim()) return;

    try {
      // Buscar pieza por NFC
      const { data: piece, error } = await api
        .from('work_order_pieces')
        .select(`
          *,
          work_order:work_orders!work_order_pieces_work_order_id_fkey(*)
        `)
        .eq('nfc_uid', nfcInput.trim())
        .single();

      if (error || !piece) {
        toast({
          title: 'Pieza no encontrada',
          description: `No se encontró pieza con NFC: ${nfcInput}`,
          variant: 'destructive',
        });
        setNfcInput('');
        return;
      }

      // Buscar OT de corte asociada
      const wo = workOrders.find(w => w.id === piece.work_order_id || w.parent_work_order_id === piece.work_order_id);
      if (wo) {
        handleStartWork(wo);
      } else {
        toast({
          title: 'Sin OT de corte',
          description: 'Esta pieza no tiene una OT de corte pendiente',
          variant: 'destructive',
        });
      }
      setNfcInput('');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
      setNfcInput('');
    }
  };

  const handleStartWork = async (wo: WorkOrder) => {
    if (activeWO) {
      toast({
        title: 'Ya tienes una OT activa',
        description: 'Finaliza la OT actual antes de iniciar otra',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await api
        .from('work_orders')
        .update({
          status: 'in_progress',
          assigned_to: user?.id,
          started_at: new Date().toISOString(),
        })
        .eq('id', wo.id);

      if (error) throw error;

      setActiveWO({ ...wo, started_at: new Date().toISOString() });
      setElapsedSeconds(0);
      setTimerStatus('normal');

      toast({
        title: 'Trabajo iniciado',
        description: `OT ${wo.order_number} en progreso`,
      });

      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleFinishWork = async () => {
    if (!activeWO) return;

    setFinishing(true);
    try {
      // 1. Marcar OT como completada
      const { error: woError } = await api
        .from('work_orders')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          notes: workNotes || null,
        })
        .eq('id', activeWO.id);

      if (woError) throw woError;

      // 2. Determinar siguiente paso
      let nextStep = '';
      let nextShelf = '';

      if (activeWO.is_composite || activeWO.requires_inserts) {
        const shelfComp = shelves.find(s => s.code === 'EST-COMP');
        await api.from('work_orders').insert({
          order_number: `${activeWO.order_number.replace('/CUT', '')}/ASM`,
          product_name: activeWO.product_name,
          customer: activeWO.customer,
          quantity: activeWO.quantity,
          priority: activeWO.priority,
          status: 'pending',
          workflow_type: 'assembly',
          source_shelf_id: shelfComp?.id,
          parent_work_order_id: activeWO.parent_work_order_id,
          requires_paint: activeWO.requires_paint,
          requires_inserts: activeWO.requires_inserts,
          is_composite: activeWO.is_composite,
          product_model_id: activeWO.product_model_id,
        });
        nextStep = 'OT de Ensamblaje creada';
        nextShelf = 'EST-COMP';
      } else if (activeWO.requires_paint) {
        const shelfPaint = shelves.find(s => s.code === 'EST-PINT');
        await api.from('work_orders').insert({
          order_number: `${activeWO.order_number.replace('/CUT', '')}/PNT`,
          product_name: activeWO.product_name,
          customer: activeWO.customer,
          quantity: activeWO.quantity,
          priority: activeWO.priority,
          status: 'pending',
          workflow_type: 'painting',
          source_shelf_id: shelfPaint?.id,
          parent_work_order_id: activeWO.parent_work_order_id,
          requires_paint: true,
          product_model_id: activeWO.product_model_id,
        });
        nextStep = 'OT de Pintura creada';
        nextShelf = 'EST-PINT';
      } else {
        nextStep = 'Pieza lista para despacho';
        nextShelf = 'EST-TERM';
      }

      toast({
        title: 'Trabajo completado',
        description: `${nextStep}. Mover pieza a ${nextShelf}`,
      });

      setFinishDialogOpen(false);
      setActiveWO(null);
      setWorkNotes('');
      setElapsedSeconds(0);
      if (timerRef.current) clearInterval(timerRef.current);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setFinishing(false);
    }
  };

  const handleCancelWork = async () => {
    if (!activeWO) return;

    try {
      await api
        .from('work_orders')
        .update({
          status: 'pending',
          assigned_to: null,
          started_at: null,
        })
        .eq('id', activeWO.id);

      setActiveWO(null);
      setWorkNotes('');
      if (timerRef.current) clearInterval(timerRef.current);
      loadData();

      toast({
        title: 'Trabajo cancelado',
        description: 'La OT vuelve al pool de pendientes',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  // Filtrar estanterías por nivel del operario
  const visibleShelves = shelves.filter(shelf => {
    if (operatorLevel === 'A') return true;
    if (operatorLevel === 'B') return shelf.min_skill_level !== 'A';
    return shelf.min_skill_level === 'C';
  });

  // Agrupar OTs por estantería
  const getWorkOrdersForShelf = (shelfId: string) => {
    return workOrders.filter(wo => wo.source_shelf_id === shelfId && wo.status === 'pending');
  };

  const timerBgClass = timerStatus === 'exceeded' ? 'bg-red-100' :
                       timerStatus === 'warning' ? 'bg-yellow-100' : 'bg-green-100';
  const timerTextClass = timerStatus === 'exceeded' ? 'text-red-700' :
                         timerStatus === 'warning' ? 'text-yellow-700' : 'text-green-700';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Scissors className="h-6 w-6" />
              Estación de Corte Taller
            </h1>
            <p className="text-muted-foreground">
              Tu nivel: <Badge className={`${SKILL_LEVEL_CONFIG[operatorLevel].bg} ${SKILL_LEVEL_CONFIG[operatorLevel].text}`}>
                {operatorLevel} - {SKILL_LEVEL_CONFIG[operatorLevel].label}
              </Badge>
            </p>
          </div>
        </div>

        {/* TRABAJO ACTIVO */}
        {activeWO ? (
          <Card className={`border-2 ${timerStatus === 'exceeded' ? 'border-red-500 bg-red-50' : timerStatus === 'warning' ? 'border-yellow-500 bg-yellow-50' : 'border-green-500 bg-green-50'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  OT EN PROGRESO: {activeWO.order_number}
                </span>
                <Button variant="ghost" size="sm" onClick={handleCancelWork}>
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Cronómetro */}
                <div className={`${timerBgClass} rounded-xl p-6 text-center`}>
                  <Timer className={`h-8 w-8 mx-auto mb-2 ${timerTextClass}`} />
                  <p className={`text-4xl font-mono font-bold ${timerTextClass}`}>
                    {formatTime(elapsedSeconds)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Estimado: {activeWO.estimated_duration_minutes || 60} min
                  </p>
                  {timerStatus === 'warning' && (
                    <Badge className="mt-2 bg-yellow-500">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      80% del tiempo
                    </Badge>
                  )}
                  {timerStatus === 'exceeded' && (
                    <Badge className="mt-2 bg-red-500">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Tiempo excedido
                    </Badge>
                  )}
                </div>

                {/* Foto del producto */}
                <div
                  className="bg-white rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors min-h-[200px]"
                  onClick={() => activeWO.product_model?.image_url && setImageModalOpen(true)}
                >
                  {activeWO.product_model?.image_url ? (
                    <img
                      src={activeWO.product_model.image_url}
                      alt={activeWO.product_name}
                      className="max-h-[200px] object-contain rounded-lg"
                    />
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Sin imagen</p>
                    </div>
                  )}
                </div>

                {/* Info del producto */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-muted-foreground text-xs">Producto</Label>
                    <p className="font-bold text-lg">{activeWO.product_name}</p>
                    {activeWO.product_model?.code && (
                      <p className="text-sm text-muted-foreground">Código: {activeWO.product_model.code}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Cliente</Label>
                    <p className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {activeWO.customer}
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Cantidad</Label>
                      <p className="font-bold">{activeWO.quantity} piezas</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Estantería</Label>
                      <Badge className={SKILL_LEVEL_CONFIG[activeWO.source_shelf?.min_skill_level || 'C'].bg}>
                        {activeWO.source_shelf?.code}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {activeWO.requires_paint && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        <Palette className="h-3 w-3 mr-1" />
                        Pintura
                      </Badge>
                    )}
                    {activeWO.requires_inserts && (
                      <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                        <Puzzle className="h-3 w-3 mr-1" />
                        Insertos
                      </Badge>
                    )}
                    {activeWO.is_composite && (
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                        <Package className="h-3 w-3 mr-1" />
                        Compuesta
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Notas y acciones */}
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Notas del trabajo</Label>
                  <Textarea
                    placeholder="Observaciones, problemas encontrados..."
                    value={workNotes}
                    onChange={(e) => setWorkNotes(e.target.value)}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end">
                  <Button size="lg" onClick={() => setFinishDialogOpen(true)} className="px-8">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Finalizar Corte
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Escáner NFC Prominente */}
            <Card className="border-2 border-dashed border-primary/50 bg-primary/5">
              <CardContent className="py-8">
                <div className="flex items-center gap-4 max-w-xl mx-auto">
                  <ScanLine className="h-12 w-12 text-primary flex-shrink-0" />
                  <div className="flex-1">
                    <Input
                      ref={nfcInputRef}
                      placeholder="Escanea el chip NFC de la pieza para iniciar..."
                      value={nfcInput}
                      onChange={(e) => setNfcInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleNfcScan()}
                      className="h-14 text-lg border-2"
                    />
                  </div>
                  <Button size="lg" onClick={handleNfcScan} className="h-14 px-6">
                    Buscar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Estanterías como tarjetas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleShelves.map((shelf) => {
                const shelfWOs = getWorkOrdersForShelf(shelf.id);
                const config = SKILL_LEVEL_CONFIG[shelf.min_skill_level];

                return (
                  <Card
                    key={shelf.id}
                    className={`${config.bg} border-2 ${config.border}`}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className={`flex items-center justify-between ${config.text}`}>
                        <span className="text-xl font-bold">{shelf.code}</span>
                        <Badge className={`${config.bg} ${config.text} border ${config.border}`}>
                          {shelfWOs.length} OTs
                        </Badge>
                      </CardTitle>
                      <p className={`text-sm ${config.text} opacity-75`}>
                        Nivel {shelf.min_skill_level} - {config.label}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                      {shelfWOs.length === 0 ? (
                        <div className="text-center py-8 opacity-50">
                          <Package className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Sin OTs pendientes</p>
                        </div>
                      ) : (
                        shelfWOs.map((wo) => (
                          <Card
                            key={wo.id}
                            className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => handleStartWork(wo)}
                          >
                            <CardContent className="p-3">
                              <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                  <p className="font-mono text-sm font-bold truncate">
                                    {wo.order_number}
                                  </p>
                                  <p className="text-sm truncate">{wo.product_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {wo.customer}
                                  </p>
                                  <div className="flex gap-1 mt-2">
                                    {wo.requires_paint && (
                                      <Palette className="h-4 w-4 text-blue-500" title="Pintura" />
                                    )}
                                    {wo.requires_inserts && (
                                      <Puzzle className="h-4 w-4 text-orange-500" title="Insertos" />
                                    )}
                                    {wo.is_composite && (
                                      <Package className="h-4 w-4 text-purple-500" title="Compuesta" />
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <Badge className={PRIORITY_COLORS[wo.priority]} variant="outline">
                                    {wo.priority}
                                  </Badge>
                                  <Button size="sm" className="mt-2">
                                    <Play className="h-3 w-3 mr-1" />
                                    Iniciar
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {visibleShelves.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg text-muted-foreground">
                    No hay estanterías disponibles para tu nivel
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Modal imagen grande */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{activeWO?.product_name}</DialogTitle>
          </DialogHeader>
          {activeWO?.product_model?.image_url && (
            <img
              src={activeWO.product_model.image_url}
              alt={activeWO.product_name}
              className="w-full object-contain max-h-[70vh]"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de finalización */}
      <AlertDialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar finalización</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-4">Tiempo trabajado: <strong>{formatTime(elapsedSeconds)}</strong></p>
              Al finalizar, la pieza se moverá a:
              <div className="mt-2 p-3 bg-muted rounded-lg">
                {activeWO?.is_composite || activeWO?.requires_inserts ? (
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    <strong>EST-COMP</strong> (Ensamblaje)
                  </div>
                ) : activeWO?.requires_paint ? (
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    <strong>EST-PINT</strong> (Pintura)
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    <strong>EST-TERM</strong> (Terminadas)
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleFinishWork} disabled={finishing}>
              {finishing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
